import { Fragment } from 'react/jsx-runtime'
import type { User } from '../../../utils/loginUtils'

import { useEffect, useState } from 'react'
import {
  getTemplateInformation,
  saveNewTemplate,
  updateTemplate,
  type template,
} from '../../../utils/templateUtils'
import TemplateMeal from './TemplateMeal'

import '../Calendar/Calendar.scss'
import './Template.scss'
import { DAYS_OF_WEEK, type MealType } from '../../../utils/calendarUtils'
import { getRecipeList, type recipe } from '../../../utils/recipeUtils'
import { getMessage } from '../../../utils/messageUtils'
import Popup, { type ExtraButton } from '../../PopupForm/Popup'
import PopupError from '../../PopupForm/PopupError'
import Spinner from '../../Spinner/Spinner'
import CollapsibleSection from '../../CollapsibleSection'

interface TemplateProps {
  user: User
  isMobile: boolean
}

function Template({ user, isMobile }: TemplateProps) {
  const [templateInfo, setTemplateInfo] = useState<template[] | undefined>(
    undefined
  )
  const [currentTemplate, setCurrentTemplate] = useState<template | undefined>(
    undefined
  )
  const [dayMobile, setDayMobile] = useState(0)

  const [recipeList, setRecipeList] = useState<recipe[] | undefined>(undefined)
  const [loadRecipeList, setLoadRecipeList] = useState(true)

  // Loading & Error
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false)
  const [isLoadingRecipes, setIsLoadingRecipes] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  // Popup
  const [popupOpen, setPopupOpen] = useState(false)
  const [popupMessage, setPopupMessage] = useState('')
  const [popupChildren, setPopupChildren] = useState<React.ReactNode>(null)
  const [popupOnOk, setPopupOnOk] = useState<() => void>(() => {})
  const [popupExtraButtons, setPopupExtraButtons] = useState<
    ExtraButton[] | undefined
  >(undefined)

  // New template popup
  const [newTemplatePopupOpen, setNewTemplatePopupOpen] = useState(false)
  const [creatingNewTemplate, setCreatingNewTemplate] = useState(false)
  const [newTemplateName, setNewTemplateName] = useState('')

  useEffect(() => {
    const fetchWeekInfo = async () => {
      setIsLoadingTemplate(true)
      try {
        const data = await getTemplateInformation(user.id!)
        if (!data.success) {
          setErrorMessage(getMessage('template_load_error'))
          return
        }
        setTemplateInfo(data.templates)
        if (data.templates && data.templates.length > 0) {
          let defaultTemplate = data.templates.find(t => t.default_template)
          if (!defaultTemplate) {
            defaultTemplate = data.templates[0]
          }
          setCurrentTemplate(defaultTemplate)
        }
      } catch {
        setErrorMessage(getMessage('template_load_error'))
      } finally {
        setIsLoadingTemplate(false)
      }
    }
    fetchWeekInfo()
  }, [user.id])

  useEffect(() => {
    const fetchRecipeList = async () => {
      setIsLoadingRecipes(true)
      try {
        const data = await getRecipeList(user.id!)
        if (data.success) {
          setRecipeList(data.recipes)
          setLoadRecipeList(false)
        } else {
          setErrorMessage(getMessage('template_recipes_load_error'))
        }
      } catch {
        setErrorMessage(getMessage('template_recipes_load_error'))
      } finally {
        setIsLoadingRecipes(false)
      }
    }

    if (loadRecipeList || !recipeList) {
      fetchRecipeList()
    }
  }, [recipeList, loadRecipeList, user.id])

  const generateNewTemplate = (): template => {
    const newTemplate: template = {
      id: -1,
      name: newTemplateName || getMessage('template_default'),
      default_template: false,
      days: Array(7).fill({
        desayuno: null,
        almuerzo: null,
        comida: null,
        merienda: null,
        cena: null,
      }),
      changed: true,
    }
    return newTemplate
  }

  const updateCurrentTemplate = (
    day: number,
    mealType: MealType,
    recipeId: number | null
  ) => {
    let newTemplate: template
    if (!currentTemplate) {
      newTemplate = generateNewTemplate()
    } else {
      newTemplate = { ...currentTemplate }
    }

    // No real change
    if (newTemplate.days[day][mealType] === recipeId) return

    newTemplate.changed = true

    const newDay = { ...newTemplate.days![day] }
    newDay[mealType] = recipeId
    newDay.modified = true
    newTemplate.days![day] = newDay
    setCurrentTemplate(newTemplate)
  }

  const saveTemplate = async () => {
    if (!currentTemplate?.changed) return

    setIsLoadingTemplate(true)
    try {
      setErrorMessage('')

      let result
      if (currentTemplate.id === -1) {
        result = await saveNewTemplate(currentTemplate, user.id!)
      } else {
        result = await updateTemplate(currentTemplate, user.id!)
      }

      if (!result.success) {
        setErrorMessage(
          getMessage('template_save_error') + (result.error || '')
        )
      } else {
        setCurrentTemplate({ ...currentTemplate, changed: false })
      }
    } catch {
      setErrorMessage(getMessage('template_save_error'))
    } finally {
      setIsLoadingTemplate(false)
    }
  }

  const previousDayClickMobile = () => {
    setDayMobile(prevDay => (prevDay === 0 ? 6 : prevDay - 1))
  }

  const nextDayClickMobile = () => {
    setDayMobile(prevDay => (prevDay === 6 ? 0 : prevDay + 1))
  }

  const getHeaderDays = (): React.ReactElement[] => {
    const headerDays: React.ReactElement[] = []
    for (let i = 0; i < 7; i++) {
      headerDays.push(
        <div
          key={`calendar-header-day-${i}`}
          className="calendar-header-day calendar-box"
        >
          {DAYS_OF_WEEK[i]}
        </div>
      )
    }
    return headerDays
  }

  const generateTemplateMeal = (
    recipeId: number | null | undefined,
    type: MealType,
    date: number
  ) => {
    return (
      <TemplateMeal
        recipeId={recipeId}
        type={type}
        date={date}
        existingRecipes={recipeList}
        isMobile={isMobile}
        openPopup={openPopup}
        closePopup={closePopup}
        setPopupOnOk={setPopupOnOk}
        updateCurrentTemplate={updateCurrentTemplate}
      />
    )
  }

  const generateTemplate = () => {
    const desayuno = []
    const almuerzo = []
    const comida = []
    const merienda = []
    const cena = []
    for (let i = 0; i < 7; i++) {
      const day = currentTemplate?.days[i]
      desayuno.push(generateTemplateMeal(day?.desayuno, 'desayuno', i))
      almuerzo.push(generateTemplateMeal(day?.almuerzo, 'almuerzo', i))
      comida.push(generateTemplateMeal(day?.comida, 'comida', i))
      merienda.push(generateTemplateMeal(day?.merienda, 'merienda', i))
      cena.push(generateTemplateMeal(day?.cena, 'cena', i))
    }

    return (
      <div className="calendar-body">
        <div className="calendar-desayuno-row calendar-row">{desayuno}</div>
        <div className="calendar-almuerzo-row calendar-row">{almuerzo}</div>
        <div className="calendar-comida-row calendar-row">{comida}</div>
        <div className="calendar-merienda-row calendar-row">{merienda}</div>
        <div className="calendar-cena-row calendar-row">{cena}</div>
      </div>
    )
  }

  const generateTemplateDayMobile = () => {
    const day = currentTemplate?.days[dayMobile]
    return (
      <div className="calendar-body">
        <div className="calendar-desayuno-row calendar-row">
          {generateTemplateMeal(day?.desayuno, 'desayuno', dayMobile)}
        </div>
        <div className="calendar-almuerzo-row calendar-row">
          {generateTemplateMeal(day?.almuerzo, 'almuerzo', dayMobile)}
        </div>
        <div className="calendar-comida-row calendar-row">
          {generateTemplateMeal(day?.comida, 'comida', dayMobile)}
        </div>
        <div className="calendar-merienda-row calendar-row">
          {generateTemplateMeal(day?.merienda, 'merienda', dayMobile)}
        </div>
        <div className="calendar-cena-row calendar-row">
          {generateTemplateMeal(day?.cena, 'cena', dayMobile)}
        </div>
      </div>
    )
  }

  const openPopup = (
    message: string,
    children: React.ReactNode,
    extraButtons: ExtraButton[]
  ) => {
    setPopupMessage(message)
    setPopupChildren(children)
    setPopupExtraButtons(extraButtons)
    setPopupOpen(true)
  }

  const closePopup = () => {
    setPopupOpen(false)
  }

  const generatePopup = () => {
    return (
      <Popup
        isOpen={popupOpen}
        message={popupMessage}
        children={popupChildren}
        extraButtons={popupExtraButtons}
        isMobile={isMobile}
        onOk={popupOnOk}
        onClose={() => setPopupOpen(false)}
      />
    )
  }

  const newTemplatePopupOnOk = () => {
    if (creatingNewTemplate) {
      const newTemplate = generateNewTemplate()
      setTemplateInfo([...(templateInfo || []), newTemplate])
      setCurrentTemplate(newTemplate)
      setCreatingNewTemplate(false)
      setNewTemplatePopupOpen(false)
    } else if (currentTemplate) {
      const newTemplate = {
        ...currentTemplate,
        name: newTemplateName,
        changed: true,
      }
      setCurrentTemplate(newTemplate)

      const index = templateInfo?.findIndex(t => t.id === currentTemplate.id)
      if (index !== undefined && index >= 0) {
        const newTemplateInfo = [...(templateInfo || [])]
        newTemplateInfo[index] = { ...newTemplate }
        setTemplateInfo(newTemplateInfo)
      }

      setNewTemplatePopupOpen(false)
    }
  }

  const generateNewTemplatePopup = () => {
    const labelClass = `pop-up-form-label ${isMobile ? 'pop-up-form-label-mobile' : ''}`
    const fieldClass = `pop-up-form-field ${isMobile ? 'pop-up-form-field-mobile' : ''}`
    const children = (
      <div
        className={`pop-up-form new-template-pop-up-main-content ${isMobile ? 'pop-up-form-mobile new-template-pop-up-main-content-mobile' : ''}`}
      >
        <div className="pop-up-form-row">
          <span className={labelClass}>{getMessage('name')}:</span>
          <input
            className={fieldClass}
            type="text"
            placeholder={getMessage('template_name')}
            value={newTemplateName}
            onChange={e => setNewTemplateName(e.target.value)}
          />
        </div>
      </div>
    )
    return (
      <Popup
        isOpen={newTemplatePopupOpen}
        message={getMessage('template_new')}
        children={children}
        isMobile={isMobile}
        onOk={newTemplatePopupOnOk}
        onClose={() => setNewTemplatePopupOpen(false)}
      />
    )
  }

  const handleTemplateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedId = e.target.value
    if (selectedId === 'new') {
      setCreatingNewTemplate(true)
      setNewTemplateName('')
      setNewTemplatePopupOpen(true)
    } else {
      const selected = templateInfo?.find(t => t.id === parseInt(selectedId))
      if (selected) {
        setCurrentTemplate(selected)
      }
    }
  }

  const generateTemplateSelector = () => {
    if (!templateInfo || templateInfo.length === 0) {
      return (
        <div className="calendar-selector-content">
          {currentTemplate?.name || getMessage('template_default')}
        </div>
      )
    }
    return (
      <select
        className="calendar-selector-content"
        value={currentTemplate?.id}
        onChange={handleTemplateChange}
      >
        {templateInfo.map(t => (
          <option key={t.id} value={t.id}>
            {t.name}
          </option>
        ))}
        {!templateInfo.some(t => t.id === -1) && (
          <option value="new">{`+ ${getMessage('template_new')}`}</option>
        )}
      </select>
    )
  }

  const setAsDefaultTemplate = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!currentTemplate) return
    setCurrentTemplate({
      ...currentTemplate,
      default_template: e.target.checked,
      changed: true,
    })
  }

  const editButtonClick = () => {
    if (!currentTemplate || currentTemplate.id === -1) return

    setNewTemplateName(currentTemplate.name)
    setNewTemplatePopupOpen(true)
  }

  const generateTemplateControls = () => {
    return (
      <>
        <label className="calendar-selector-default-label">
          <input
            type="checkbox"
            className="calendar-selector-default-checkbox"
            checked={currentTemplate?.default_template}
            onChange={setAsDefaultTemplate}
          />
          {getMessage('template_set_default')}
        </label>
        <button
          className={`template-edit-button ${currentTemplate?.id === -1 ? 'template-edit-button-hidden' : ''}`}
          onClick={currentTemplate?.id !== -1 ? editButtonClick : undefined}
          disabled={currentTemplate?.id === -1}
        >
          {getMessage('template_edit')}
        </button>
      </>
    )
  }

  const generateMobileTemplateOptions = () => {
    return (
      <div className="template-options-bar">
        {generateTemplateSelector()}
        <div
          className={`template-controls ${
            isMobile ? 'template-controls-mobile' : 'template-controls-desktop'
          }`}
        >
          {generateTemplateControls()}
        </div>
      </div>
    )
  }

  const generateDesktopOptions = () => {
    return (
      <>
        {generateTemplateSelector()}
        <div className="template-controls template-controls-desktop">
          {generateTemplateControls()}
        </div>
        {currentTemplate?.changed && (
          <button
            className="calendar-selector-save-button"
            onClick={saveTemplate}
          >
            {getMessage('save_changes')}
          </button>
        )}
      </>
    )
  }

  const generateDateSelector = () => {
    if (isMobile) {
      return (
        <>
          <CollapsibleSection
            title={getMessage('options')}
            defaultOpen={false}
            isMobile={isMobile}
          >
            {generateMobileTemplateOptions()}
          </CollapsibleSection>
          <div className="calendar-selector-bar">
            <div
              className="calendar-selector-arrow"
              onClick={previousDayClickMobile}
            >
              {'<'}
            </div>
            <div className="calendar-selector-content">
              {DAYS_OF_WEEK[dayMobile]}
            </div>
            <div
              className="calendar-selector-arrow"
              onClick={nextDayClickMobile}
            >
              {'>'}
            </div>
          </div>
        </>
      )
    } else {
      return (
        <div className="calendar-selector-bar">{generateDesktopOptions()}</div>
      )
    }
  }

  return (
    <Fragment>
      <div
        className={`calendar-container ${
          isMobile ? 'calendar-container-mobile' : ''
        }`}
      >
        {generateDateSelector()}
        {errorMessage && (
          <PopupError
            errorMessage={errorMessage}
            setErrorMessage={setErrorMessage}
          />
        )}
        <div className="calendar-body-container">
          {!isMobile && (
            <div className="calendar-header calendar-row">
              {getHeaderDays()}
            </div>
          )}
          {isMobile ? generateTemplateDayMobile() : generateTemplate()}
        </div>
      </div>
      {generatePopup()}
      {generateNewTemplatePopup()}
      <Spinner
        isOpen={isLoadingTemplate || isLoadingRecipes}
        overlay={true}
        message="Loading..."
      />
    </Fragment>
  )
}

export default Template
